import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  CircularProgress,
  Alert,
  AlertTitle,
} from '@mui/material';
import { MyLocation } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import InteractiveMap, {
  Area,
  AreaList,
} from '../../components/InteractiveMap';
import {
  fetchServiceAreas,
  updateArea,
  UpdateAreaInput,
  createNewArea,
  CreateAreaInput,
  deleteArea,
  updateAreaAssignments,
} from '../../services/areaService';
import { GamifyToast } from '../../common/CustomToasts';
import AreaAssignmentModal from './components/AreaAssignmentModal';
import { usePermissions } from '../../context/PermissionsContext/PermissionsContext';
import { PermissionKeys } from '../../context/PermissionsContext/permissionTypes';
import useOrgData from '../../hooks/useOrgData';

const PageContainer = styled(Box)(({ theme }) => ({
  padding: '125px 100px 60px 100px',
  height: '100vh',
  maxHeight: '100vh',
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: theme.custom.background1,
  color: theme.custom.text,

  '@media (max-width: 768px)': {
    padding: '125px 50px 60px 50px',
  },
}));

const MapWithSidebarContainer = styled(Box)(({ theme }) => ({
  flex: 1,
  display: 'flex',
  gap: 16,
  marginTop: 16,
  minHeight: '600px',
  backgroundColor: theme.custom.background1,
}));

const MapContainer = styled(Box)(({ theme }) => ({
  flex: 1,
  minHeight: '600px',
  backgroundColor: theme.custom.background2,
  borderRadius: '8px',
  overflow: 'hidden',
}));

const LoadingContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '400px',
  flexDirection: 'column',
  gap: 16,
  backgroundColor: theme.custom.background2,
  borderRadius: '8px',
  color: theme.custom.textLight,
}));

const EditModeAlert = styled(Alert)(({ theme }) => ({
  marginBottom: 16,
  backgroundColor: 'lightgreen',
  color: 'white',
  '& .MuiAlert-icon': {
    color: theme.custom.primary,
  },
  '& .MuiAlertTitle-root': {
    color: theme.custom.text,
  },
  '& .MuiTypography-root': {
    color: theme.custom.text,
  },
}));

const MapPage: React.FC = () => {
  const [areas, setAreas] = useState<Area[]>([]);
  const [selectedAreaId, setSelectedAreaId] = useState<string | undefined>();
  const [isLoadingAreas, setIsLoadingAreas] = useState(true);
  const [userLocation, setUserLocation] = useState<{
    latitude: number;
    longitude: number;
  } | null>(null);
  const [mapCenter, setMapCenter] = useState({
    longitude: -122.4194,
    latitude: 37.7749,
    zoom: 13,
  });
  const [isLoadingLocation, setIsLoadingLocation] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingAreaId, setEditingAreaId] = useState<string | undefined>();
  const [isUpdatingArea, setIsUpdatingArea] = useState(false);
  const [editingAreaName, setEditingAreaName] = useState<string>('');

  // New states for area creation
  const [drawingCoords, setDrawingCoords] = useState<
    Array<{ lat: number; lon: number }>
  >([]);
  const [pendingAreaId, setPendingAreaId] = useState<string | undefined>(); // Track temporary area being created
  const [isCreatingNewArea, setIsCreatingNewArea] = useState(false);

  // Area assignment modal states
  const [assignmentModalOpen, setAssignmentModalOpen] = useState(false);
  const [selectedAreaForAssignment, setSelectedAreaForAssignment] =
    useState<Area | null>(null);

  // Ref to prevent duplicate location requests
  const locationRequestedRef = useRef(false);

  // Ref to store the map's flyTo function
  const mapFlyToRef = useRef<
    ((longitude: number, latitude: number, zoomLevel?: number) => void) | null
  >(null);

  // Add useOrgData hook
  const { orgData } = useOrgData();

  // Add permissions hook
  const { isSuperAdmin, hasPermission, checkPermission } = usePermissions();

  // Helper function to check canCreateAreas based on the specified criteria
  const checkCanCreateAreas = useCallback((): boolean => {
    // Check if user is super admin
    if (isSuperAdmin()) {
      return true;
    }

    // Check Territory permissions from the role-based system with proper scope enforcement
    // For creation, we need to handle "onlyMe" scope differently since there's no existing area
    const createPermission = checkPermission(
      PermissionKeys.Territory_CreateArea
    );

    // For create operations, "onlyMe", "myTeam", and "everyone" should all allow creation
    // since the user will be the creator of the new area
    if (
      createPermission &&
      createPermission !== 'none' &&
      createPermission !== null
    ) {
      return true;
    }

    // Check global.me properties for the criteria mentioned
    // @ts-ignore
    const currentUser = (global as any)?.me;
    if (!currentUser) {
      return false;
    }

    // Criteria 1: Organization administrators
    if (currentUser.isAdmin) {
      return true;
    }

    // Criteria 2: Users with manager functional roles
    if (
      currentUser.functionalRoles &&
      Array.isArray(currentUser.functionalRoles)
    ) {
      const hasManagerRole = currentUser.functionalRoles.some((role: string) =>
        role.toLowerCase().includes('manager')
      );
      if (hasManagerRole) {
        return true;
      }
    }

    // Criteria 3: Users whose member type matches the organization's canCreateAreas permission setting
    if (currentUser.canCreateAreas === true) {
      return true;
    }

    return false;
  }, [checkPermission, isSuperAdmin]);

  // Helper function to check edit permission with scope enforcement
  const checkCanEditAreas = useCallback(
    (area?: Area): boolean => {
      if (isSuperAdmin()) {
        return true;
      }

      // For area editing, we need to check scope against the area's creator/owner
      const userIds = area?.createdBy ? [area.createdBy] : [];
      const hasPermissionResult = hasPermission(
        PermissionKeys.Territory_UpdateArea,
        userIds
      );

      return hasPermissionResult;
    },
    [hasPermission, isSuperAdmin]
  );

  // Helper function to check delete permission with scope enforcement
  const checkCanDeleteAreas = useCallback(
    (area?: Area): boolean => {
      if (isSuperAdmin()) {
        return true;
      }

      // For area deletion, we need to check scope against the area's creator/owner
      const userIds = area?.createdBy ? [area.createdBy] : [];
      const hasPermissionResult = hasPermission(
        PermissionKeys.Territory_DeleteArea,
        userIds
      );

      return hasPermissionResult;
    },
    [hasPermission, isSuperAdmin]
  );

  // Memoize permission checks - for general UI, check without specific area context
  const canCreateAreas = checkCanCreateAreas();
  const canEditAreas = checkCanEditAreas(); // General edit permission
  const canDeleteAreas = checkCanDeleteAreas(); // General delete permission

  useEffect(() => {
    // Prevent duplicate location requests (React StrictMode can cause double mounting)
    if (!locationRequestedRef.current) {
      locationRequestedRef.current = true;
      getCurrentLocation();
    }
    loadAreas();
  }, []);

  const loadAreas = async (): Promise<void> => {
    try {
      setIsLoadingAreas(true);

      const fetchedAreas = await fetchServiceAreas();
      setAreas(fetchedAreas);

      if (fetchedAreas.length === 0) {
        GamifyToast.info(
          'No Areas Found',
          'No areas found for this organization. You may need to create some areas first.'
        );
      }
    } catch (error: any) {
      console.error('Failed to load areas:', error);
      let errorMessage = 'Failed to load areas. ';

      if (error?.message) {
        errorMessage += error.message;
      } else if (error?.errors && error.errors.length > 0) {
        errorMessage += error.errors[0].message;
      } else {
        errorMessage += 'Please check your network connection and try again.';
      }

      GamifyToast.error('Area Loading Error', errorMessage);
    } finally {
      setIsLoadingAreas(false);
    }
  };

  const getCurrentLocation = () => {
    setIsLoadingLocation(true);

    if (!navigator.geolocation) {
      setIsLoadingLocation(false);
      // Don't show error toast for automatic location requests
      return;
    }

    // Check if we're in a secure context
    if (
      window.location.protocol !== 'https:' &&
      window.location.hostname !== 'localhost' &&
      window.location.hostname !== '127.0.0.1'
    ) {
      setIsLoadingLocation(false);
      // Don't show error toast for automatic location requests
      return;
    }

    // Check permissions API if available
    if (navigator.permissions) {
      navigator.permissions
        .query({ name: 'geolocation' })
        .then(() => {
          // Proceed regardless of permission state - let the browser handle it
          requestLocation();
        })
        .catch(() => {
          // Fallback if permissions API fails
          requestLocation();
        });
    } else {
      requestLocation();
    }
  };

  const requestLocation = () => {
    // Safari-friendly options
    const options = {
      enableHighAccuracy: false, // Try with lower accuracy first for Safari
      timeout: 20000, // Longer timeout for Safari
      maximumAge: 0, // Force fresh location request
    };

    // Add a small delay for Safari - sometimes it needs a moment
    setTimeout(() => {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const location = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          };
          setUserLocation(location);

          // Use flyTo function directly if available
          if (mapFlyToRef.current) {
            mapFlyToRef.current(location.longitude, location.latitude, 15);
          } else {
            // Fallback to state update if flyTo not available yet
            setMapCenter({
              longitude: location.longitude,
              latitude: location.latitude,
              zoom: 15,
            });
          }

          setIsLoadingLocation(false);
          // Only show success message for automatic location
          GamifyToast.success('Location Found', `Centered on your location`);
        },
        () => {
          setIsLoadingLocation(false);

          // Fallback to San Francisco when geolocation fails
          const defaultCenter = {
            longitude: -122.4194,
            latitude: 37.7749,
            zoom: 13,
          };

          // Use flyTo function directly if available
          if (mapFlyToRef.current) {
            mapFlyToRef.current(
              defaultCenter.longitude,
              defaultCenter.latitude,
              13
            );
          } else {
            // Fallback to state update if flyTo not available yet
            setMapCenter(defaultCenter);
          }
        },
        options
      );
    }, 100); // Small delay for Safari
  };

  const handleAreaSelect = (area: Area) => {
    setSelectedAreaId(area.id);

    // Center map on the area
    if (area.coords && area.coords.length > 0) {
      // Calculate center of the area
      const centerLat =
        area.coords.reduce((sum, coord) => sum + coord.lat, 0) /
        area.coords.length;
      const centerLon =
        area.coords.reduce((sum, coord) => sum + coord.lon, 0) /
        area.coords.length;

      // Use flyTo function directly if available
      if (mapFlyToRef.current) {
        mapFlyToRef.current(centerLon, centerLat, 14);
      } else {
        // Fallback to state update if flyTo not available yet
        setMapCenter({
          longitude: centerLon,
          latitude: centerLat,
          zoom: 14,
        });
      }
    }
  };

  const handleAreaClick = (area: Area) => {
    setSelectedAreaId(area.id);
  };

  // Callback to store the flyTo function when map is ready
  const handleMapReady = (
    flyToFunction: (
      longitude: number,
      latitude: number,
      zoomLevel?: number
    ) => void
  ) => {
    mapFlyToRef.current = flyToFunction;
  };

  const handleAreaEdit = async (
    areaId: string,
    updates: Partial<UpdateAreaInput>
  ) => {
    try {
      setIsUpdatingArea(true);

      // Find the original area to pass to updateArea
      const originalArea = areas.find((area) => area.id === areaId);

      const updatedArea = await updateArea(
        {
          id: areaId,
          ...updates,
        },
        originalArea
      );

      // Update the areas list with the updated area
      setAreas((prevAreas) =>
        prevAreas.map((area) => (area.id === areaId ? updatedArea : area))
      );

      GamifyToast.success(
        'Area Updated',
        `Successfully updated ${updatedArea.name}`
      );

      // Exit edit mode
      setIsEditMode(false);
      setEditingAreaId(undefined);
    } catch (error: any) {
      console.error('Failed to update area:', error);
      let errorMessage = 'Failed to update area. ';

      if (error?.message) {
        errorMessage += error.message;
      } else if (error?.errors && error.errors.length > 0) {
        errorMessage += error.errors[0].message;
      } else {
        errorMessage += 'Please check your network connection and try again.';
      }

      GamifyToast.error('Update Error', errorMessage);
    } finally {
      setIsUpdatingArea(false);
    }
  };

  const handleAreaDelete = async (areaId: string) => {
    const area = areas.find((a) => a.id === areaId);

    // Check permission against specific area
    if (!checkCanDeleteAreas(area)) {
      GamifyToast.error(
        'Permission Denied',
        'You do not have permission to delete this area. Contact your administrator.'
      );
      return;
    }

    if (!area) return;

    // Show confirmation dialog
    const confirmed = window.confirm(
      `Are you sure you want to delete "${area.name}"?\n\nThis action cannot be undone.`
    );

    if (!confirmed) return;

    try {
      setIsUpdatingArea(true);

      await deleteArea(areaId);

      // Remove the area from the list
      setAreas((prevAreas) => prevAreas.filter((area) => area.id !== areaId));

      // Clear selection if the deleted area was selected
      if (selectedAreaId === areaId) {
        setSelectedAreaId(undefined);
      }

      GamifyToast.success('Area Deleted', `Successfully deleted ${area.name}`);
    } catch (error: any) {
      console.error('Failed to delete area:', error);
      let errorMessage = 'Failed to delete area. ';

      if (error?.message) {
        errorMessage += error.message;
      } else if (error?.errors && error.errors.length > 0) {
        errorMessage += error.errors[0].message;
      } else {
        errorMessage += 'Please check your network connection and try again.';
      }

      GamifyToast.error('Delete Error', errorMessage);
    } finally {
      setIsUpdatingArea(false);
    }
  };

  const handleCoordinateAdd = useCallback(
    (areaId: string, coordinate: { lat: number; lon: number }) => {
      setAreas((prevAreas) =>
        prevAreas.map((area) =>
          area.id === areaId
            ? { ...area, coords: [...area.coords, coordinate] }
            : area
        )
      );
      GamifyToast.success('Coordinate Added', 'New coordinate added to area');
    },
    []
  );

  const handleCoordinateMove = useCallback(
    (
      areaId: string,
      coordinateIndex: number,
      newCoordinate: { lat: number; lon: number }
    ) => {
      setAreas((prevAreas) =>
        prevAreas.map((area) =>
          area.id === areaId
            ? {
                ...area,
                coords: area.coords.map((coord, index) =>
                  index === coordinateIndex ? newCoordinate : coord
                ),
              }
            : area
        )
      );
    },
    []
  );

  const handleCoordinateDelete = useCallback(
    (areaId: string, coordinateIndex: number) => {
      setAreas((prevAreas) =>
        prevAreas.map((area) =>
          area.id === areaId
            ? {
                ...area,
                coords: area.coords.filter(
                  (_, index) => index !== coordinateIndex
                ),
              }
            : area
        )
      );

      // If we're deleting coordinates from a pending area (during creation),
      // also update the drawingCoords state
      if (isCreatingNewArea && pendingAreaId === areaId) {
        setDrawingCoords((prevCoords) =>
          prevCoords.filter((_, index) => index !== coordinateIndex)
        );
      }

      GamifyToast.success('Coordinate Deleted', 'Coordinate removed from area');
    },
    [isCreatingNewArea, pendingAreaId]
  );

  const startEditingArea = (areaId: string) => {
    const area = areas.find((a) => a.id === areaId);

    // Check permission against specific area
    if (!checkCanEditAreas(area)) {
      GamifyToast.error(
        'Permission Denied',
        'You do not have permission to edit this area. Contact your administrator.'
      );
      return;
    }

    setIsEditMode(true);
    setEditingAreaId(areaId);
    setSelectedAreaId(areaId);
    setEditingAreaName(area?.name || '');
    GamifyToast.info(
      'Edit Mode Active',
      'Edit the area name above, click on the map to add coordinates, drag existing points to move them, or right-click points to delete them'
    );
  };

  const cancelEditingArea = () => {
    if (pendingAreaId === editingAreaId) {
      // Remove the temporary area if canceling creation
      setAreas((prev) => prev.filter((area) => area.id !== editingAreaId));
      setPendingAreaId(undefined);
      setIsCreatingNewArea(false);
      setDrawingCoords([]);
      GamifyToast.info('Area Creation Cancelled', 'Temporary area removed.');
    }

    setIsEditMode(false);
    setEditingAreaId(undefined);
    setEditingAreaName('');
    setSelectedAreaId(undefined);

    // Only reload areas if we're not canceling a pending area
    if (pendingAreaId !== editingAreaId) {
      loadAreas();
    }
  };

  const saveAreaChanges = async () => {
    if (!editingAreaId) return;

    const editingArea = areas.find((area) => area.id === editingAreaId);
    if (!editingArea) return;

    // Check if this is a pending area that needs to be created
    if (pendingAreaId === editingAreaId) {
      // This is a new area that needs to be created in the backend
      try {
        setIsUpdatingArea(true);

        const createAreaInput: CreateAreaInput = {
          name: editingAreaName.trim() || editingArea.name,
          coords: editingArea.coords,
        };

        const newArea = await createNewArea(createAreaInput);

        // @ts-ignore - global.me is available in the application
        const currentUser = (global as any)?.me;

        // Ensure createdBy is set properly
        const areaWithCreator: Area = {
          ...newArea,
          createdBy: newArea.createdBy || currentUser?.id,
          lastUpdatedBy: newArea.lastUpdatedBy || currentUser?.id,
        };

        // Replace the temporary area with the real one and sort the list alphabetically
        setAreas((prev) => {
          const updatedAreas = prev.map((area) =>
            area.id === editingAreaId ? areaWithCreator : area
          );
          // Sort areas alphabetically by name
          return updatedAreas.sort((a, b) => a.name.localeCompare(b.name));
        });

        // Clear all creation-related states
        setPendingAreaId(undefined);
        setIsCreatingNewArea(false);
        setDrawingCoords([]);

        // Exit edit mode
        setIsEditMode(false);
        setEditingAreaId(undefined);
        setEditingAreaName('');

        // Set selection to trigger scroll to the new area's position in the sorted list
        setSelectedAreaId(areaWithCreator.id);

        GamifyToast.success(
          'Area Created',
          `Successfully created ${areaWithCreator.name}`
        );

        // Don't automatically reload areas - keep the newly created area visible
        // The area is already in the list from the previous setAreas call
      } catch (error: any) {
        console.error('Failed to create area:', error);
        let errorMessage = 'Failed to create area. ';

        if (error?.message) {
          errorMessage += error.message;
        } else {
          errorMessage += 'Please check your network connection and try again.';
        }

        GamifyToast.error('Creation Error', errorMessage);
      } finally {
        setIsUpdatingArea(false);
      }
    } else {
      // This is an existing area being edited
      const updates: Partial<UpdateAreaInput> = {
        coords: editingArea.coords,
      };

      // Only include name if it has changed
      if (editingAreaName.trim() && editingAreaName !== editingArea.name) {
        updates.name = editingAreaName.trim();
      }

      await handleAreaEdit(editingAreaId, updates);
    }
  };

  const centerOnUser = () => {
    if (userLocation) {
      // Use flyTo function directly if available
      if (mapFlyToRef.current) {
        mapFlyToRef.current(userLocation.longitude, userLocation.latitude, 15);
      } else {
        // Fallback to state update if flyTo not available yet
        setMapCenter({
          longitude: userLocation.longitude,
          latitude: userLocation.latitude,
          zoom: 15,
        });
      }
      setSelectedAreaId(undefined);
      GamifyToast.success('Location', 'Centered on your location');
    } else {
      // Manual location request with detailed feedback
      setIsLoadingLocation(true);

      if (!navigator.geolocation) {
        setIsLoadingLocation(false);
        GamifyToast.info(
          'Location Info',
          'Geolocation is not supported by this browser.'
        );
        return;
      }

      if (
        window.location.protocol !== 'https:' &&
        window.location.hostname !== 'localhost' &&
        window.location.hostname !== '127.0.0.1'
      ) {
        setIsLoadingLocation(false);
        GamifyToast.info(
          'Location Info',
          'Geolocation requires HTTPS. Please use a secure connection.'
        );
        return;
      }

      const options = {
        enableHighAccuracy: false,
        timeout: 20000,
        maximumAge: 0,
      };

      setTimeout(() => {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const location = {
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
            };
            setUserLocation(location);

            // Use flyTo function directly if available
            if (mapFlyToRef.current) {
              mapFlyToRef.current(location.longitude, location.latitude, 15);
            } else {
              // Fallback to state update if flyTo not available yet
              setMapCenter({
                longitude: location.longitude,
                latitude: location.latitude,
                zoom: 15,
              });
            }

            setIsLoadingLocation(false);
            GamifyToast.success(
              'Location Found',
              `Centered on your location: ${location.latitude.toFixed(
                4
              )}, ${location.longitude.toFixed(4)}`
            );
          },
          (error) => {
            setIsLoadingLocation(false);

            let errorMessage = '';
            switch (error.code) {
              case error.PERMISSION_DENIED:
                errorMessage =
                  'Location access was denied. Enable location access in your browser settings.';
                break;
              case error.POSITION_UNAVAILABLE:
                errorMessage =
                  'Location information is unavailable. Using default location (San Francisco).';
                break;
              case error.TIMEOUT:
                errorMessage =
                  'Location request timed out. This sometimes happens in Safari. Using default location (San Francisco).';
                break;
              default:
                errorMessage =
                  'An unknown error occurred while retrieving location. Using default location (San Francisco).';
                break;
            }

            // Use flyTo function directly if available
            if (mapFlyToRef.current) {
              mapFlyToRef.current(-122.4194, 37.7749, 13);
            } else {
              // Fallback to state update if flyTo not available yet
              setMapCenter({
                longitude: -122.4194,
                latitude: 37.7749,
                zoom: 13,
              });
            }

            GamifyToast.info('Location Error', errorMessage);
          },
          options
        );
      }, 100);
    }
  };

  const handleCreateNewArea = () => {
    // Check permission before allowing area creation
    if (!canCreateAreas) {
      GamifyToast.error(
        'Permission Denied',
        'You do not have permission to create areas. Contact your administrator.'
      );
      return;
    }

    // @ts-ignore - global.me is available in the application
    const currentUser = (global as any)?.me;

    // Create a new temporary area and add to the beginning of the list
    const tempAreaId = `temp-${Date.now()}-${Math.random()
      .toString(36)
      .substr(2, 9)}`;
    const tempArea: Area = {
      id: tempAreaId,
      name: '', // No auto-populated name
      coords: [],
      createdBy: currentUser?.id, // Set the current user as creator
      lastUpdatedBy: currentUser?.id,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      isDeleted: false,
    };

    // Add the temporary area to the BEGINNING of the list
    setAreas((prev) => [tempArea, ...prev]);

    // Select the temporary area and enter edit mode for naming
    setSelectedAreaId(tempAreaId);
    setEditingAreaId(tempAreaId);
    setEditingAreaName('');
    setIsEditMode(true);
    setPendingAreaId(tempAreaId); // Mark this as pending creation
    setIsCreatingNewArea(true);

    // Start collecting coordinates
    setDrawingCoords([]);

    GamifyToast.info(
      'New Area Started',
      'Click on the map to add boundary points. Enter a name in the list and click "Create Area" to save.'
    );
  };

  const handleDrawingClick = useCallback(
    (location: { latitude: number; longitude: number }) => {
      if (isCreatingNewArea && pendingAreaId) {
        const newCoord = { lat: location.latitude, lon: location.longitude };
        const updatedCoords = [...drawingCoords, newCoord];
        setDrawingCoords(updatedCoords);

        // Update the temporary area with the new coordinates
        setAreas((prev) =>
          prev.map((area) =>
            area.id === pendingAreaId
              ? {
                  ...area,
                  coords: updatedCoords,
                }
              : area
          )
        );

        // Show progress feedback
        if (updatedCoords.length >= 3) {
          GamifyToast.info(
            'Drawing Progress',
            `${updatedCoords.length} points added. Enter a name and click "Create Area" to save.`
          );
        } else {
          GamifyToast.info(
            'Drawing Progress',
            `${updatedCoords.length} points added. Need at least 3 points to create an area.`
          );
        }
      }
    },
    [isCreatingNewArea, pendingAreaId, drawingCoords]
  );

  const cancelAreaCreation = useCallback(() => {
    if (pendingAreaId) {
      // Remove the temporary area
      setAreas((prev) => prev.filter((area) => area.id !== pendingAreaId));
      setPendingAreaId(undefined);
    }

    setIsCreatingNewArea(false);
    setDrawingCoords([]);
    setIsEditMode(false);
    setEditingAreaId(undefined);
    setEditingAreaName('');
    setSelectedAreaId(undefined);

    GamifyToast.info('Area Creation Cancelled', 'New area removed from list.');
  }, [pendingAreaId]);

  // Remove the old finishDrawing and cancelDrawing functions
  // Update keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (isCreatingNewArea) {
        if (event.key === 'Escape') {
          cancelAreaCreation();
        }
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [isCreatingNewArea, cancelAreaCreation]);

  // Area assignment handlers
  const handleAreaAssignmentClick = (area: Area) => {
    setSelectedAreaForAssignment(area);
    setAssignmentModalOpen(true);
  };

  const handleUpdateAssignments = async (
    areaId: string,
    users: any[],
    circles: any[]
  ) => {
    const originalArea = areas.find((a) => a.id === areaId);
    if (!originalArea) return;

    // Update the area with both user and circle assignments
    const updatedArea = await updateAreaAssignments(
      areaId,
      users,
      circles,
      originalArea
    );

    // Update local state
    setAreas((prevAreas) =>
      prevAreas.map((area) => (area.id === areaId ? updatedArea : area))
    );
  };

  const closeAssignmentModal = () => {
    setAssignmentModalOpen(false);
    setSelectedAreaForAssignment(null);
  };

  if (isLoadingAreas) {
    return (
      <PageContainer>
        <Typography
          variant="h4"
          gutterBottom
          sx={{ color: (theme) => theme.custom.text }}
        >
          Interactive Map
        </Typography>
        <LoadingContainer>
          <CircularProgress size={60} />
          <Typography
            variant="h6"
            sx={{ color: (theme) => theme.custom.textLight }}
          >
            Loading areas...
          </Typography>
        </LoadingContainer>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Typography
        variant="h4"
        gutterBottom
        sx={{ color: (theme) => theme.custom.text }}
      >
        Interactive Map
      </Typography>

      <Box
        sx={{
          mb: 2,
        }}
      >
        {userLocation ? (
          <Typography
            variant="body2"
            sx={{ color: (theme) => theme.custom.primary }}
          >
            📍 Location found: {userLocation.latitude.toFixed(4)},{' '}
            {userLocation.longitude.toFixed(4)}
          </Typography>
        ) : (
          <Typography
            variant="body2"
            sx={{ color: (theme) => theme.custom.textLight }}
          >
            📍 Using default location (San Francisco)
          </Typography>
        )}
      </Box>

      {isCreatingNewArea && (
        <EditModeAlert severity="warning">
          <AlertTitle>Creating New Area</AlertTitle>
          <Typography variant="body2">
            • <strong>Click</strong> on the map to add boundary points (
            {drawingCoords.length} points added)
            <br />• <strong>Enter a name</strong> in the area list (required)
            <br />• Need at least <strong>3 points</strong> and a{' '}
            <strong>name</strong> to create area
            <br />• Press <strong>Escape</strong> to cancel
          </Typography>
        </EditModeAlert>
      )}

      {isEditMode && editingAreaId && !isCreatingNewArea && (
        <EditModeAlert severity="info">
          <AlertTitle>Editing Mode Active</AlertTitle>
          <Typography variant="body2">
            • <strong>Edit area name</strong> in the area list on the left
            <br />• <strong>Click</strong> on the map to add new coordinates
            <br />• <strong>Drag</strong> orange points to move existing
            coordinates
            <br />• <strong>Right-click</strong> orange points to delete them
            <br />• Click <strong>&quot;Save Changes&quot;</strong> to persist
            your edits
          </Typography>
        </EditModeAlert>
      )}

      {/* Permission-based alert */}
      {!canCreateAreas && !canEditAreas && !canDeleteAreas && (
        <Alert
          severity="info"
          sx={{
            mb: 2,
            backgroundColor: (theme) => theme.custom.background2,
            color: (theme) => theme.custom.text,
            border: (theme) => `1px solid ${theme.custom.stroke}`,
          }}
        >
          <AlertTitle sx={{ color: (theme) => theme.custom.text }}>
            Limited Access
          </AlertTitle>
          You have view-only access to areas. Contact your administrator for
          area management permissions.
        </Alert>
      )}

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: (theme) => theme.custom.background1,
        }}
      >
        <Typography
          variant="h6"
          component="div"
          sx={{ color: (theme) => theme.custom.text }}
        >
          Areas ({areas.length})
        </Typography>

        <Box sx={{ display: 'flex', gap: 1 }}>
          {/* Area creation actions */}
          {isCreatingNewArea && canCreateAreas && (
            <>
              <Button
                variant="contained"
                onClick={saveAreaChanges}
                disabled={
                  isUpdatingArea ||
                  !editingAreaName.trim() ||
                  drawingCoords.length < 3
                }
                color="primary"
              >
                {isUpdatingArea ? 'Creating...' : 'Create Area'}
              </Button>
              <Button
                variant="outlined"
                onClick={cancelAreaCreation}
                color="error"
              >
                Cancel Creation
              </Button>
            </>
          )}

          {/* Areas mode actions */}
          {!isCreatingNewArea && !isEditMode && canCreateAreas && (
            <Button
              variant="contained"
              onClick={handleCreateNewArea}
              color="primary"
            >
              Create New Area
            </Button>
          )}

          {!isCreatingNewArea && selectedAreaId && !isEditMode && (
            <>
              {(() => {
                const selectedArea = areas.find((a) => a.id === selectedAreaId);
                return (
                  <>
                    {checkCanEditAreas(selectedArea) && (
                      <Button
                        variant="outlined"
                        onClick={() => startEditingArea(selectedAreaId)}
                        color="secondary"
                      >
                        Edit Area
                      </Button>
                    )}
                    {checkCanDeleteAreas(selectedArea) && (
                      <Button
                        variant="outlined"
                        onClick={() => handleAreaDelete(selectedAreaId)}
                        color="error"
                        disabled={isUpdatingArea}
                      >
                        {isUpdatingArea ? 'Deleting...' : 'Delete Area'}
                      </Button>
                    )}
                  </>
                );
              })()}
            </>
          )}

          {!isCreatingNewArea &&
            isEditMode &&
            editingAreaId &&
            canEditAreas && (
              <>
                <Button
                  variant="contained"
                  onClick={saveAreaChanges}
                  disabled={isUpdatingArea}
                  color="primary"
                >
                  {isUpdatingArea ? 'Saving...' : 'Save Changes'}
                </Button>
                <Button
                  variant="outlined"
                  onClick={cancelEditingArea}
                  disabled={isUpdatingArea}
                >
                  Cancel Edit
                </Button>
              </>
            )}

          {/* Location button - always available */}
          <IconButton
            onClick={centerOnUser}
            color="primary"
            title={
              isLoadingLocation
                ? 'Getting your location...'
                : userLocation
                ? 'Center on my location'
                : 'Get my location'
            }
            disabled={isLoadingLocation}
          >
            {isLoadingLocation ? (
              <CircularProgress size={20} />
            ) : (
              <MyLocation />
            )}
          </IconButton>
        </Box>
      </Box>

      <MapWithSidebarContainer>
        <AreaList
          areas={areas}
          selectedAreaId={selectedAreaId}
          onAreaSelect={handleAreaSelect}
          editingAreaId={editingAreaId}
          isEditMode={isEditMode}
          editingAreaName={editingAreaName}
          onAreaNameChange={setEditingAreaName}
          onAreaAssignmentClick={handleAreaAssignmentClick}
        />

        <MapContainer>
          <InteractiveMap
            initialViewState={mapCenter}
            drawingEnabled={isCreatingNewArea}
            areas={areas}
            selectedAreaId={selectedAreaId}
            userLocation={userLocation}
            onDrawingClick={isCreatingNewArea ? handleDrawingClick : undefined}
            drawingCoords={drawingCoords}
            onAreaClick={handleAreaClick}
            isEditMode={isEditMode && canEditAreas}
            editingAreaId={editingAreaId}
            onCoordinateAdd={canEditAreas ? handleCoordinateAdd : undefined}
            onCoordinateMove={canEditAreas ? handleCoordinateMove : undefined}
            onCoordinateDelete={
              canEditAreas ? handleCoordinateDelete : undefined
            }
            mapboxToken={orgData?.configParms?.features?.route?.mapboxKey}
            onMapReady={handleMapReady}
          />
        </MapContainer>
      </MapWithSidebarContainer>

      {/* Area Assignment Modal */}
      {assignmentModalOpen && selectedAreaForAssignment && (
        <AreaAssignmentModal
          open={assignmentModalOpen}
          onClose={closeAssignmentModal}
          onUpdateAssignments={handleUpdateAssignments}
          area={selectedAreaForAssignment}
        />
      )}
    </PageContainer>
  );
};

export default MapPage;
